import * as React from 'react';
import { alpha } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../themePrimitives';

export const inputsCustomizations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: 'border-box',
        transition: 'all 100ms ease-in',
        '&:focus-visible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        fontWeight: 500,
        textTransform: 'none',
        boxShadow: 'none',
        border: '1px solid transparent',
        backgroundColor: 'transparent',
        color: (theme.vars || theme).palette.text.primary,
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
        minHeight: '1.75rem',
        padding: '0.25rem 0.75rem',
        fontSize: '0.88rem',
        // Slightly stronger preset tint for hover/active so buttons darken visibly
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.06)',
          borderColor: 'rgba(0,0,0,0.14)',
        },
        '&:active': {
          backgroundColor: 'rgba(0,0,0,0.10)',
          borderColor: 'rgba(0,0,0,0.20)',
        },
        '&:disabled': {
          opacity: 0.5,
          pointerEvents: 'none',
        },
        variants: [
          {
            props: { variant: 'contained' },
            style: {
              backgroundColor: gray[50],
              color: '#000000',
              borderColor: gray[50],
              minHeight: '1.75rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.88rem',
              '&:hover': {
                /* slightly darker white so white/primary buttons just darken a bit */
                backgroundColor: '#cccccc',
                borderColor: '#e6e6e6',
              },
              '&:active': {
                backgroundColor: '#eeeeee',
                borderColor: '#dcdcdc',
              }
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              backgroundColor: '#101010',
              color: (theme.vars || theme).palette.text.primary,
              // Slightly darker base border for outlined buttons
              borderColor: '#2b2b2b',
              minHeight: '1.75rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.88rem',
              '&:hover': {
                backgroundColor: '#171717',
                borderColor: '#3b3b3b',
              },
              '&:active': {
                backgroundColor: '#202020',
                borderColor: '#3b3b3b',
              },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              backgroundColor: 'transparent',
              color: (theme.vars || theme).palette.text.primary,
              border: 'none',
              minHeight: '1.75rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.88rem',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.06)',
              },
              '&:active': {
                backgroundColor: 'rgba(0,0,0,0.10)',
              },
              ...theme.applyStyles('dark', {
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
                '&:active': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        color: (theme.vars || theme).palette.text.primary,
        border: '1px solid transparent',
        backgroundColor: 'transparent',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
        width: '1.75rem',
        height: '1.75rem',
        padding: '0.15rem',
        fontSize: '0.88rem',
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.06)',
          borderColor: 'rgba(0,0,0,0.14)',
        },
        '&:active': {
          backgroundColor: 'rgba(0,0,0,0.10)',
          borderColor: 'rgba(0,0,0,0.20)',
        },
        '&:disabled': {
          opacity: 0.5,
          pointerEvents: 'none',
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: '1.75rem',
              height: '1.75rem',
              padding: '0.15rem',
              [`& .${svgIconClasses.root}`]: { fontSize: '0.95rem' },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              width: '2rem',
              height: '2rem',
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '10px',
        boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: brand[500],
        },
        ...theme.applyStyles('dark', {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: '#fff',
          },
          boxShadow: `0 4px 16px ${alpha(brand[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 16px',
        textTransform: 'none',
        borderRadius: '10px',
        fontWeight: 500,
        ...theme.applyStyles('dark', {
          color: gray[400],
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
          [`&.${toggleButtonClasses.selected}`]: {
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: '1px solid ',
        borderColor: alpha(gray[300], 0.8),
        boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
        backgroundColor: alpha(gray[100], 0.4),
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {
          borderColor: brand[300],
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: '2px',
          borderColor: brand[400],
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          '&:hover': {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: '0 0 0 1.5px hsl(210, 0%, 0%) inset',
          backgroundColor: alpha(gray[900], 0.8),
          '&:hover': {
            borderColor: brand[300],
          },
          '&.Mui-focusVisible': {
            borderColor: brand[400],
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: 'none',
      },
      input: {
        '&::placeholder': {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        padding: '8px 12px',
        color: (theme.vars || theme).palette.text.primary,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        backgroundColor: (theme.vars || theme).palette.background.default,
        transition: 'border 120ms ease-in',
        '&:hover': {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          borderColor: brand[400],
        },
        ...theme.applyStyles('dark', {
          '&:hover': {
            borderColor: gray[500],
          },
        }),
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              height: '2.25rem',
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              height: '2.5rem',
            },
          },
        ],
      }),
      notchedOutline: {
        border: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
      }),
    },
  },
};